import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./layout/Layout";
import { Login } from "./pages/Login";
import { AiInfo } from "./pages/AiInfo";
import { SharedFile } from "./pages/SharedFile";
import { SharedFolder } from "./pages/SharedFolder";
import { StepUpGate } from "./features/security";
import { PublicHome } from "./pages/PublicHome";
import { LockedPage } from "./pages/LockedPage";

/* ─── Die Seiten hinter der Anmeldung kommen erst, wenn man sie aufruft ───
 *
 * Bis hierher lag alles in einer Datei: 2,4 MB, und die musste vollständig
 * da sein, bevor der Anmeldeschirm auch nur erschien. Auf dem Telefon über
 * Mobilfunk sind das die Sekunden, in denen nichts passiert — und weil jeder
 * neue Stand einen neuen Namen bekommt, fielen sie nach jeder Änderung
 * wieder an.
 *
 * Jetzt bekommt jede Seite ihre eigene Datei. Wer sich anmeldet, lädt den
 * Anmeldeschirm und sonst nichts; was er danach anklickt, kommt dann. Die
 * Dateien bleiben abrufbar, wer ihre Adresse aus dem Bündel liest — das war
 * die Abmachung (b): der Gewinn beim Aufbau, ohne das Risiko, dass eine
 * abgelaufene Sitzung die Seite weiß macht.
 *
 * `lazy` will einen Standardexport, unsere Seiten sind benannt — daher
 * jeweils das kleine `.then`.
 */
const seite = <T extends string>(
  laden: () => Promise<Record<string, unknown>>, name: T,
) => lazy(() => laden().catch(nachNeuemStand).then((m) => ({ default: m[name] as React.ComponentType })));

const NEULADEN = "seite:neuladen";

/** A deploy renames every chunk, so a tab that was already open asks for a
 *  file that is gone and the page stays broken until someone reloads by hand.
 *  One reload fetches the new index; the stamp keeps a genuinely missing
 *  chunk from looping. */
function nachNeuemStand(fehler: unknown): never | Promise<never> {
  let zuletzt = 0;
  try {
    zuletzt = Number(sessionStorage.getItem(NEULADEN)) || 0;
  } catch { /* private mode — then without the guard */ }
  if (!navigator.onLine || Date.now() - zuletzt < 10_000) throw fehler;
  try {
    sessionStorage.setItem(NEULADEN, String(Date.now()));
  } catch { /* s.o. */ }
  location.reload();
  return new Promise<never>(() => {});
}

const Dashboard = seite(() => import("./pages/Dashboard"), "Dashboard");
const Calendar = seite(() => import("./pages/Calendar"), "Calendar");
const Mail = seite(() => import("./pages/Mail"), "Mail");
const Files = seite(() => import("./pages/Files"), "Files");
// Die Maschinenseite bringt xterm.js mit — sie war schon vorher getrennt.
const PC = seite(() => import("./pages/PC"), "PC");
const Sites = seite(() => import("./pages/Sites"), "Sites");
const SiteDetail = seite(() => import("./pages/SiteDetail"), "SiteDetail");
const Git = seite(() => import("./pages/Git"), "Git");
const GitRepo = seite(() => import("./pages/GitRepo"), "GitRepo");
const ReportsPage = seite(() => import("./features/reports"), "ReportsPage");
const Settings = seite(() => import("./pages/Settings"), "Settings");
const Automations = seite(() => import("./pages/Automations"), "Automations");
const Grades = seite(() => import("./pages/dhbw/Grades"), "Grades");
const Lichter = seite(() => import("./pages/zimmer/Lichter"), "Lichter");
const AdminPage = seite(() => import("./features/admin/AdminPage"), "AdminPage");
import { useAuthed, useIch } from "./lib/useAuthed";
import { darf } from "./api";
import { HinweisLayer, TooltipLayer } from "./ui";
import { ServerOffline } from "./components/ServerOffline";

/** Umschließt eine Seite, die eine Anmeldung braucht.
 *
 *  Früher eine Weiterleitung auf /login. Das war unhöflich in beide
 *  Richtungen: der Besucher verlor die Adresse, die er aufgerufen hatte, und
 *  nach dem Anmelden landete er irgendwo anders. Jetzt bleibt die Route
 *  stehen und zeigt, dass hier etwas ist — der Login oben rechts füllt sie
 *  ohne Sprung. */
function RequireAuth({ perm, children }: { perm?: string; children: React.ReactNode }) {
  const authed = useAuthed();
  const ich = useIch();
  if (!authed) return <LockedPage />;
  // Angemeldet, aber nicht für diesen Bereich freigegeben. Eine eigene
  // Antwort, kein Anmeldeschirm: wer hier landet, ist angemeldet — er soll
  // nicht glauben, seine Sitzung sei abgelaufen.
  void ich;
  if (perm && !darf(perm)) return <NichtFreigegeben />;
  return <>{children}</>;
}

/** „Diesen Bereich hat dir niemand freigegeben." */
function NichtFreigegeben() {
  return (
    <div className="page" style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
      <div style={{ textAlign: "center", maxWidth: "24rem" }}>
        <h1 style={{ marginBottom: "0.4rem" }}>Not available</h1>
        <p className="muted">
          Your account does not have this area. Ask the administrator for access.
        </p>
      </div>
    </div>
  );
}

/** Dashboard für Angemeldete, öffentliche Startseite für alle anderen. */
function Home() {
  return useAuthed() ? <Dashboard /> : <PublicHome />;
}

export function App() {
  return (
    <HinweisLayer>
      {/* Eine Schicht für die ganze Anwendung — jeder `title` wird dadurch
          zur themengerechten Blase, auch auf den öffentlichen Seiten. */}
      <TooltipLayer />
      {/* Der eine Satz fuer den Fall, dass gar nichts mehr antwortet.
          Ueber allem, weil er fuer alles gilt — auch fuer den
          Anmeldeschirm. */}
      <ServerOffline />
      {/* Eine Grenze für alle Seiten.
          Jede kommt in ihrer eigenen Datei und ist beim ersten Aufruf einen
          Augenblick unterwegs. Der Platzhalter ist eine leere Seite in voller
          Höhe und kein Kringel in der Mitte: so ruckt das Layout nicht, wenn
          der Inhalt eintrifft. */}
      <Suspense fallback={<div className="page" aria-busy="true" />}>
      <Routes>
      <Route path="/login" element={<Login />} />

      {/* Ohne Anmeldung: ein Agent, der sich erst anmelden müsste, kann die
          Anleitung dazu nicht lesen. */}
      <Route path="/ai" element={<AiInfo />} />

      {/* Eine einzelne Datei hinter einem Link — ohne Anmeldung, ohne
          Layout. Wer den Link bekommt, soll den Inhalt sehen und sonst
          nichts: nicht, wo die Datei liegt, und nicht, was daneben liegt.
          Kurzer Pfad, weil er in fremde Hände geht und getippt werden
          können soll. */}
      <Route path="/f/:token" element={<SharedFile />} />
      {/* Ein ganzer Ordner hinter einem Link — derselbe Gedanke, nur größer. */}
      <Route path="/d/:token" element={<SharedFolder />} />
      {/* Das Layout steht jetzt für alle — samt Sidebar, damit ohne
          Anmeldung sichtbar bleibt, was es gibt. Was Daten braucht, sperrt
          sich einzeln über <RequireAuth>. */}
      <Route
        element={
          <>
            <Layout />
            <StepUpGate />
          </>
        }
      >
        {/* Flat top-level — every primary feature is one click away.
            Cross-section filters (DHBW / Privat) live inside each page. */}
        <Route path="/"          element={<Home />} />
        <Route path="/calendar"  element={<RequireAuth perm="calendar"><Calendar /></RequireAuth>}  />
        <Route path="/mail"      element={<RequireAuth perm="mail"><Mail /></RequireAuth>}      />
        {/* Notizen gibt es nicht mehr als eigenen Ort: eine Notiz ist eine
            Markdown-Datei, und die Ablage kann Dateien anlegen, bearbeiten
            und einzeln per Link freigeben. Zwei Orte für „mein Text liegt
            hier" waren zwei Editoren, zwei Suchen und bei jeder neuen Datei
            die Frage, welcher der beiden gemeint ist. */}
        <Route path="/notes" element={<Navigate to="/storage" replace />} />
        {/* Ohne RequireAuth: die Seite zeigt abgemeldet den öffentlichen
            Bestand und lässt dort auch anlegen. */}
        {/* „Storage", nicht „Files": die Seite ist der Arbeitsplatz für
            alles Abgelegte — Dateien, Notizen, später auch ein Git-Repo als
            Modul. Der alte Name zeigt dorthin. */}
        <Route path="/storage"   element={<Files />}     />
        <Route path="/files"     element={<Navigate to="/storage" replace />} />
        <Route path="/grades"    element={<RequireAuth perm="grades"><Grades /></RequireAuth>}    />
        {/* Ohne RequireAuth: abgemeldet zeigt dieselbe Seite die öffentlich
            gestellten Seiten. Eine zweite „öffentliche Übersicht" daneben
            wäre ein zweiter Ort für dieselbe Sache. */}
        <Route path="/sites"     element={<Sites />}     />
        <Route path="/sites/:slug" element={<RequireAuth perm="sites"><SiteDetail /></RequireAuth>} />
        <Route path="/git"       element={<RequireAuth perm="git"><Git /></RequireAuth>}       />
        <Route path="/reports"   element={<RequireAuth perm="settings"><ReportsPage /></RequireAuth>} />
        {/* Ein Repo mit seinem Inhalt — eigene Seite statt Dialog, damit der
            Dateibaum Platz hat und ein Klick reicht. */}
        <Route path="/git/:name" element={<RequireAuth perm="git"><GitRepo /></RequireAuth>}   />
        <Route path="/lights"    element={<RequireAuth perm="lights"><Lichter /></RequireAuth>}   />
        {/* Automatik ist eine eigene Seite, keine Einstellung: „in zehn
            Minuten alles aus" ist eine Handlung. */}
        <Route path="/automation" element={<RequireAuth perm="settings"><Automations /></RequireAuth>} />
        {/* Der Container ist keine eigene Seite mehr, sondern der öffentliche
            Reiter der Dateien: derselbe Bestand, nur die Hälfte, die ohne
            Anmeldung sichtbar ist. Alte Adressen zeigen dorthin. */}
        <Route path="/container" element={<Navigate to="/storage" replace />} />
        <Route path="/ablage"    element={<Navigate to="/storage" replace />} />
        {/* Früher eine eigene Seite, jetzt ein Filter in den Notizen. */}
        <Route path="/notes/public" element={<Navigate to="/storage" replace />} />
        <Route
          path="/machine"
          element={
            <RequireAuth perm="pc"><PC /></RequireAuth>
          }
        />
        {/* Sicherheit und Protokoll sind Einstellungen, keine eigenen
            Bereiche — sie leben als Reiter in /settings. Die alten Adressen
            zeigen auf den passenden Reiter, damit Lesezeichen halten. */}
        <Route path="/settings"  element={<RequireAuth><Settings /></RequireAuth>}  />
        {/* Die Verwalterkonsole. Kein eigenes Recht: sie hängt an der Rolle
            und an der Vollmacht auf Zeit, und beides prüft der Server. */}
        <Route path="/admin"     element={<RequireAuth><AdminPage /></RequireAuth>} />
        <Route path="/security"  element={<Navigate to="/settings?tab=security" replace />} />
        <Route path="/logs"      element={<Navigate to="/settings?tab=logs" replace />} />

        {/* Legacy redirects — old bookmarks/PWA shortcuts keep working. */}
        <Route path="/dhbw"            element={<Navigate to="/" replace />} />
        <Route path="/dhbw/timetable"  element={<Navigate to="/calendar?filter=dhbw" replace />} />
        <Route path="/dhbw/grades"     element={<Navigate to="/grades" replace />} />
        <Route path="/dhbw/mail"       element={<Navigate to="/mail?view=dhbw" replace />} />
        <Route path="/dhbw/notes"      element={<Navigate to="/storage" replace />} />
        <Route path="/dhbw/files"      element={<Navigate to="/storage" replace />} />

        <Route path="/privat"          element={<Navigate to="/" replace />} />
        <Route path="/privat/calendar" element={<Navigate to="/calendar?filter=privat" replace />} />
        <Route path="/privat/mail"     element={<Navigate to="/mail" replace />} />
        <Route path="/privat/notes"    element={<Navigate to="/storage" replace />} />
        <Route path="/privat/files"    element={<Navigate to="/storage" replace />} />

        {/* Die Adresse folgt der Überschrift: „Machine" liegt unter /machine,
            „Lights" unter /lights. Die alten Namen zeigen dorthin, damit
            Lesezeichen und die Verknüpfungen der App weiter funktionieren. */}
        <Route path="/pc"              element={<Navigate to="/machine" replace />} />
        <Route path="/lichter"         element={<Navigate to="/lights" replace />} />
        <Route path="/zimmer"          element={<Navigate to="/lights" replace />} />
        <Route path="/zimmer/lichter"  element={<Navigate to="/lights" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </HinweisLayer>
  );
}
